import {useDispatch} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {metadaApi} from '@dms/api/base';
import {useLazyGetCustomerV2Query} from '@dms/api/customer';
import {
  GetDirectSaleResponse,
  usePatchDirectSaleGetCurrentPriceMutation,
  usePatchPartsRequestCancelByOriginEntityMutation,
  usePostIssueNotesMutation,
  usePostPartsRequestMutation,
} from '@dms/api/warehouse';
import {
  WAREHOUSE_GRID_CODES,
  getNaturalPersonFullName,
  handleApiError,
  refreshDmsDatagrid,
} from '@dms/shared';

import {Nullish} from 'shared/web';

import {type GridDataChange} from 'features/eag-grid';

import {getDirectSaleItemRowsWithAction} from '../utils/getDirectSaleItemRowsWithAction';
import {mapRowsToIssueNoteItems} from '../utils/mapRowsToIssueNoteItems';
import {mapRowsToPartsRequestItems} from '../utils/mapRowsToPartsRequestItems';

const ISSUE_ACTION_KEY = 'issue';
const REQUEST_ACTION_KEY = 'request';
const REQUEST_CANCELLATION_ACTION_KEY = 'requestCancellation';
const GET_CURRENT_PRICES_ACTION_KEY = 'getCurrentPrices';

type UseDirectSaleItemsBulkActionsParams = {
  gridData: GridDataChange | null;
  directSaleId: string;
  directSale: GetDirectSaleResponse | Nullish;
};

/**
 * "More actions" bulk operations over the direct sale's work items (T20-138676): issue
 * material, request material, request cancellation, and get current prices, each
 * applied to EVERY loaded row whose backend actions expose the corresponding key (the
 * same applicability signal the grid uses per row). Counts feed the dropdown labels;
 * rows come from onGridDataChange. The payloads mirror the per-row handlers in
 * WorkItemsSegment's actionCallback.
 *
 * Gating is strictly per action, never menu-wide: each action's in-flight state disables
 * (and spins) only its own item, so running one action leaves the others usable. Per-item
 * disabling is also what protects against double-submit, since an already-open Dropdown
 * stays interactive even when its trigger is disabled.
 */
export const useDirectSaleItemsBulkActions = (params: UseDirectSaleItemsBulkActionsParams) => {
  const dispatch = useDispatch();

  const [getCustomer, customerState] = useLazyGetCustomerV2Query();
  const [createIssueNote, issueState] = usePostIssueNotesMutation();
  const [postPartsRequest, requestState] = usePostPartsRequestMutation();
  const [cancelPartsRequest, cancellationState] =
    usePatchPartsRequestCancelByOriginEntityMutation();
  const [patchDirectSaleGetCurrentPrice, currentPricesState] =
    usePatchDirectSaleGetCurrentPriceMutation();

  const rows = params.gridData?.rows ?? [];
  const issueRows = getDirectSaleItemRowsWithAction(rows, ISSUE_ACTION_KEY);
  const requestRows = getDirectSaleItemRowsWithAction(rows, REQUEST_ACTION_KEY);
  const cancellationRows = getDirectSaleItemRowsWithAction(rows, REQUEST_CANCELLATION_ACTION_KEY);
  const currentPricesRows = getDirectSaleItemRowsWithAction(rows, GET_CURRENT_PRICES_ACTION_KEY);

  const refreshItems = () => refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseDirectSaleItem);

  const issueMaterial = () => {
    const directSaleNumber = params.directSale?.number;
    const authorizationProfileId = params.directSale?.authorizationProfileId;
    const customerId = params.directSale?.customerId;

    if (isNil(directSaleNumber)) {
      throw new Error('directSaleNumber is not defined');
    }

    if (isNil(authorizationProfileId)) {
      throw new Error('directSaleAuthorizationProfileId is not defined');
    }

    const customerDataPromise = isNotNil(customerId)
      ? getCustomer({customerId}).unwrap().catch(handleApiError)
      : Promise.resolve(null);

    return customerDataPromise.then((customerData) =>
      createIssueNote({
        directSaleId: params.directSaleId,
        body: {
          originDocId: params.directSaleId,
          originDocType: 'direct-sale',
          originDocNumber: directSaleNumber,
          authorizationProfileId,
          customerName: getNaturalPersonFullName(customerData?.foundingPerson) ?? undefined,
          items: mapRowsToIssueNoteItems(issueRows),
        },
      })
        .unwrap()
        .then(refreshItems)
        .catch(handleApiError)
    );
  };

  const requestMaterial = () =>
    postPartsRequest({
      directSaleId: params.directSaleId,
      body: {
        originEntityType: 'wrh_sale_item',
        requestItems: mapRowsToPartsRequestItems(requestRows, params.directSaleId),
      },
    })
      .unwrap()
      .then(refreshItems)
      .catch(handleApiError);

  const requestCancellation = () =>
    cancelPartsRequest({
      directSaleId: params.directSaleId,
      body: {
        originEntityId: cancellationRows.map((row) => row.id),
      },
    })
      .unwrap()
      .then(refreshItems)
      .catch(handleApiError);

  const getCurrentPrices = () =>
    patchDirectSaleGetCurrentPrice({
      directSaleId: params.directSaleId,
      body: {directSaleItemId: currentPricesRows.map((row) => row.id)},
    })
      .unwrap()
      .then(() =>
        refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseDirectSaleItem, {deselectAll: true})
      )
      // Updated item prices change the direct sale totals shown outside the grid.
      .then(() => dispatch(metadaApi.util.invalidateTags(['directSales'])))
      .catch(handleApiError);

  return {
    /** In-flight state of each action — gates (and spins) only its own item. */
    isIssueMaterialPending: customerState.isFetching || issueState.isLoading,
    isRequestMaterialPending: requestState.isLoading,
    isRequestCancellationPending: cancellationState.isLoading,
    isGetCurrentPricesPending: currentPricesState.isLoading,
    issueCount: issueRows.length,
    requestCount: requestRows.length,
    cancellationCount: cancellationRows.length,
    currentPricesCount: currentPricesRows.length,
    issueMaterial,
    requestMaterial,
    requestCancellation,
    getCurrentPrices,
  };
};
